import { useCallback, useEffect, useMemo } from "react"
import { useDispatch } from "react-redux"
import {
    listWorkerGroups,
    loadMoreWorkerGroups,
    reloadWorkerGroups
} from "../Actions/API/WorkerGroupActions"
import { APIWorkerGroup } from "../Models/API/WorkerGroup"
import { FetchState, hasValue } from "../Models/Client/FetchState"
import { hasOrgPermission } from "../Models/Client/OrgPermissionScope"
import { getItemList, getPageToFetch } from "../Models/Client/RemoteData"
import { SortExpression } from "../Models/Client/SortExpression"
import { Result } from "../Models/Utility/Result"
import { MainThunkDispatch } from "../Reducers"
import { useMyOrgRole } from "./useMyOrgRole"
import { useAppState } from "./useReduxState"

export function useWorkerGroupList(
    orgUsername: string,
    sort: SortExpression<APIWorkerGroup>
): [
    FetchState<{
        items: APIWorkerGroup[]
        total: number
    }>,
    () => Promise<void>,
    () => Promise<void>
] {
    const entry = useAppState(state => state.api.workerGroups.org[orgUsername])
    const dispatch = useDispatch<MainThunkDispatch>()
    const orgRoleFetch = useMyOrgRole(orgUsername)
    const handleLoadMore = useCallback(
        () => dispatch(loadMoreWorkerGroups(orgUsername)),
        [dispatch, orgUsername]
    )
    const handleReload = useCallback(
        () => dispatch(reloadWorkerGroups(orgUsername)),
        [dispatch, orgUsername]
    )

    useEffect(() => {
        if (
            !hasValue(orgRoleFetch) ||
            !hasOrgPermission(orgRoleFetch.value, "worker:view")
        ) {
            return
        }

        const page = entry === undefined ? 0 : getPageToFetch(entry, sort)

        if (
            page >= 0 ||
            JSON.stringify(sort) !== JSON.stringify(entry?.list?.params)
        ) {
            dispatch(listWorkerGroups(orgUsername, page, sort))
        }
    }, [dispatch, entry, orgRoleFetch, orgUsername, sort])

    return [
        useMemo(() => {
            if (entry === undefined || entry.list === undefined) {
                return undefined
            }

            return Result.ok({
                items: getItemList(entry),
                total: entry.list.total
            })
        }, [entry]),
        handleLoadMore,
        handleReload
    ]
}
